/**
 * "Rever documento na fonte" (E155.F3, ADR-0105 §2.6–§2.7): para cada documento do caso que foi
 * lido, a equipe pede que todas as informações esperadas nele sejam lidas de novo, com motivo. O
 * resumo por resultado volta na tela; o que mudou já entrou no exame e foi verificado de novo; o
 * ambíguo e o não localizado seguem para a confirmação com fonte, campo a campo.
 */
import type { AxiosInstance } from 'axios';
import { Fragment, useState } from 'react';

import { Button } from '@/components/ui';
import type { DocumentoDaFichaData } from '@/lib/ficha_da_leitura';
import {
  ReleituraRecusadaError,
  motivoCabe,
  pedirReleituraDoDocumento,
  type RecusaDaReleitura,
  type ReleituraDoDocumentoOutData,
  type ResultadoDaReleitura,
} from '@/lib/releitura_da_leitura';

import { MotivoDaReleitura, Recusa } from './ReverLeituraPartes';
import { preencher, r, t } from './textos';

const RESUMO = [
  'aplicados',
  'sem_mudanca',
  'ambiguos',
  'nao_localizados',
  'esgotados',
  'sem_forma',
] as const;

function ResumoDoDocumento({
  resposta,
  onFechar,
}: {
  resposta: ReleituraDoDocumentoOutData;
  onFechar: () => void;
}): JSX.Element {
  return (
    <div className="space-y-sc-2" data-testid="releitura-documento-resumo">
      <p role="status" className="text-body-sm font-semibold text-fg">
        {r.documento.feita}
      </p>
      <dl className="grid gap-x-sc-3 gap-y-1 text-body-sm sm:grid-cols-[auto_1fr]">
        {RESUMO.map((chave) => (
          <Fragment key={chave}>
            <dt className="text-fg-muted">{r.documento.resumo[chave]}</dt>
            <dd className="text-fg" data-testid={`releitura-documento-${chave}`}>
              {resposta[chave].length === 0
                ? r.documento.nenhum
                : resposta[chave].map((campo) => t.campo[campo]).join(', ')}
            </dd>
          </Fragment>
        ))}
      </dl>
      <p className="text-body-sm text-fg-muted">
        {preencher(r.reverificadas, { n: resposta.reverificadas })}
      </p>
      <Button type="button" size="sm" variant="ghost" onClick={onFechar}>
        {r.voltar}
      </Button>
    </div>
  );
}

export interface ReverDocumentoProps {
  client: AxiosInstance;
  caseId: string;
  documento: DocumentoDaFichaData;
  onFeito: (resultado: ResultadoDaReleitura) => void;
  onFechar: () => void;
}

export function ReverDocumento({
  client,
  caseId,
  documento,
  onFeito,
  onFechar,
}: ReverDocumentoProps): JSX.Element {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [recusa, setRecusa] = useState<RecusaDaReleitura | null>(null);
  const [resposta, setResposta] = useState<ReleituraDoDocumentoOutData | null>(null);

  async function enviar(): Promise<void> {
    setEnviando(true);
    setRecusa(null);
    try {
      const dados = await pedirReleituraDoDocumento(client, caseId, {
        documento_id: documento.documento_id,
        motivo: motivo.trim(),
      });
      setResposta(dados);
      onFeito(dados.aplicados.length > 0 ? 'mudou' : 'sem_mudanca');
    } catch (err) {
      setRecusa(err instanceof ReleituraRecusadaError ? err.recusa : 'indisponivel');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mt-sc-2 space-y-sc-2" data-testid="releitura-documento">
      <p className="text-body-sm font-semibold text-fg">
        {preencher(r.documento.titulo, { documento: documento.documento_nome })}
      </p>
      <p className="text-body-sm text-fg-muted">{r.documento.texto}</p>
      {resposta === null ? (
        <>
          <MotivoDaReleitura
            motivo={motivo}
            onChange={setMotivo}
            testId="releitura-documento-motivo"
          />
          <div className="flex flex-wrap gap-sc-2">
            <Button
              type="button"
              size="sm"
              disabled={enviando || !motivoCabe(motivo)}
              onClick={() => void enviar()}
              data-testid="releitura-documento-solicitar"
            >
              {enviando ? r.enviando : r.documento.solicitar}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onFechar}>
              {r.voltar}
            </Button>
          </div>
        </>
      ) : (
        <ResumoDoDocumento resposta={resposta} onFechar={onFechar} />
      )}
      <Recusa recusa={recusa} />
    </div>
  );
}

export interface DocumentosParaReverProps {
  client: AxiosInstance;
  caseId: string;
  documentos: readonly DocumentoDaFichaData[];
  onFeito: (resultado: ResultadoDaReleitura) => void;
}

/** Os documentos lidos do caso, cada um com "Rever documento na fonte". */
export function DocumentosParaRever({
  client,
  caseId,
  documentos,
  onFeito,
}: DocumentosParaReverProps): JSX.Element | null {
  const [aberto, setAberto] = useState<string | null>(null);
  const lidos = documentos.filter((d) => d.leitura === 'camada' || d.leitura === 'imagem');
  if (lidos.length === 0) return null;
  return (
    <div className="space-y-sc-2" data-testid="ficha-rever-documentos">
      <h4 className="text-body-md font-semibold text-fg">{r.abrirDocumento}</h4>
      <p className="max-w-prose text-body-sm text-fg-muted">{r.documento.intro}</p>
      <ul className="space-y-sc-2">
        {lidos.map((documento) => (
          <li
            key={documento.documento_id}
            className="border border-border px-sc-3 py-sc-2"
            data-testid="ficha-documento-para-rever"
            data-documento={documento.documento_id}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-sc-2">
              <p className="font-semibold text-fg">{documento.documento_nome}</p>
              {aberto === documento.documento_id ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setAberto(documento.documento_id)}
                  data-testid="ficha-rever-documento"
                >
                  {r.abrirDocumento}
                </Button>
              )}
            </div>
            {aberto === documento.documento_id ? (
              <ReverDocumento
                client={client}
                caseId={caseId}
                documento={documento}
                onFeito={onFeito}
                onFechar={() => setAberto(null)}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
